import React, { useMemo } from "react";
import Input from "../ui/Input";
import Select from "../ui/Select";
import StatusBadge from "../ui/StatusBadge";
import { colors, radius, spacing, typography } from "../ui/tokens";

const panelStyle = {
  display: "grid",
  gap: spacing.lg,
  alignContent: "start",
};

const headerStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: spacing.sm,
  flexWrap: "wrap",
  paddingBottom: spacing.sm,
  borderBottom: `1px solid ${colors.border}`,
};

const titleStyle = {
  margin: 0,
  fontSize: typography.size.base,
  fontWeight: typography.weight.bold,
  color: colors.text,
};

const subtitleStyle = {
  margin: `${spacing.xs}px 0 0 0`,
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const controlRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.md,
  alignItems: "flex-end",
};

const searchFieldWrapStyle = {
  flex: "2 1 280px",
  minWidth: "min(280px, 100%)",
};

const fieldWrapStyle = {
  flex: "1 1 160px",
  minWidth: 150,
};

const controlStyle = {
  width: "100%",
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.sm,
  borderRadius: radius.sm,
};

export default function TaskFilters({ filters, onChange }) {
  const updateField = (field, value) => {
    onChange({
      ...filters,
      [field]: value,
    });
  };

  const activeFilterCount = useMemo(() => {
    const checks = [
      String(filters?.search || "").trim().length > 0,
      filters?.status && filters.status !== "All",
      filters?.priority && filters.priority !== "All",
      filters?.framework && filters.framework !== "All",
      filters?.dueMode && filters.dueMode !== "All",
    ];
    return checks.filter(Boolean).length;
  }, [filters]);

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <div>
          <h3 style={titleStyle}>Filters</h3>
          <p style={subtitleStyle}>Refine visible tasks by state, priority, and due timeline.</p>
        </div>
        <StatusBadge
          variant="status"
          value={activeFilterCount > 0 ? "active" : "inactive"}
          label={`Active ${activeFilterCount}`}
        />
      </div>

      <div style={controlRowStyle}>
        <div style={searchFieldWrapStyle}>
          <Input
            label="Search"
            type="text"
            placeholder="Search tasks, controls..."
            value={filters.search || ""}
            onChange={(e) => updateField("search", e.target.value)}
            style={controlStyle}
          />
        </div>

        <div style={fieldWrapStyle}>
          <Select
            label="Status"
            value={filters.status}
            onChange={(e) => updateField("status", e.target.value)}
            style={controlStyle}
          >
            <option>All</option>
            <option>Not started</option>
            <option>In progress</option>
            <option>Needs review</option>
            <option>Completed</option>
          </Select>
        </div>

        <div style={fieldWrapStyle}>
          <Select
            label="Priority"
            value={filters.priority}
            onChange={(e) => updateField("priority", e.target.value)}
            style={controlStyle}
          >
            <option>All</option>
            <option>Critical</option>
            <option>High</option>
            <option>Medium</option>
            <option>Low</option>
          </Select>
        </div>

        <div style={fieldWrapStyle}>
          <Select
            label="Framework"
            value={filters.framework}
            onChange={(e) => updateField("framework", e.target.value)}
            style={controlStyle}
          >
            <option>All</option>
            <option>SOC2</option>
            <option>ISO27001</option>
            <option>Custom</option>
          </Select>
        </div>

        <div style={fieldWrapStyle}>
          <Select
            label="Due Date"
            value={filters.dueMode}
            onChange={(e) => updateField("dueMode", e.target.value)}
            style={controlStyle}
          >
            <option value="All">All</option>
            <option value="Overdue">Overdue</option>
            <option value="ThisWeek">Due this week</option>
          </Select>
        </div>
      </div>
    </div>
  );
}
